import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import Dashboard, { newUid, type DashItem } from "../widgets/Dashboard";
import { DEFAULT_LAYOUT } from "../widgets/registry";
import { useAuthGate } from "../lib/auth";
import { cacheSet, useSWR } from "../lib/swr";
import {
  banks,
  dashboard as dashboardApi,
  errMessage,
  transactions,
  type DashboardLayout,
} from "../api/client";

function itemsFromLayout(layout: DashboardLayout): DashItem[] {
  return layout.widgets.map((w) => ({
    uid: newUid(),
    type: w.type,
    size: w.size,
    config: { ...w.config },
  }));
}

function layoutFromItems(items: DashItem[]): DashboardLayout {
  return {
    version: 1,
    widgets: items.map((i) => ({ type: i.type, size: i.size, config: i.config })),
  };
}

export default function HomePage() {
  const { user, loading } = useAuthGate();
  const [items, setItems] = useState<DashItem[] | null>(null);
  const [editing, setEditing] = useState(false);
  // Snapshot taken when entering edit mode so Abbrechen can restore it.
  const [preEdit, setPreEdit] = useState<DashItem[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // FirstRun onboarding card — deliberately outside the widget system so it
  // can't be removed before it has done its job.
  // undefined = still loading; we MUST distinguish "haven't asked yet" from
  // "asked and got zero", otherwise the FirstRunCard flashes on every
  // refresh until the GET /transactions call resolves.
  const [showFirstRunOverride, setShowFirstRunOverride] = useState<boolean | null>(null);

  // Cache-first: all three paint instantly from the last known value and
  // revalidate in the background (lib/swr.ts).
  const { data: layoutData, error: layoutError } = useSWR(
    user ? "home:layout" : null,
    () => dashboardApi.get().then((r) => r.layout ?? DEFAULT_LAYOUT),
  );
  const { data: tx } = useSWR(user ? "home:firsttx" : null, () =>
    transactions.list({ limit: 1 }),
  );
  const { data: bankData } = useSWR(user ? "home:banks" : null, () => banks.list());
  const bankConns = bankData ?? [];

  // Mirror the fetched layout into the editable items list — but never while
  // an edit is in progress, and only when the layout actually changed
  // (background revalidates return fresh but identical objects).
  const lastLayoutJson = useRef<string | null>(null);
  useEffect(() => {
    if (editing) return;
    const layout: DashboardLayout | null =
      layoutData ?? (layoutError ? DEFAULT_LAYOUT : null);
    if (!layout) return;
    const json = JSON.stringify(layout.widgets);
    if (json === lastLayoutJson.current) return;
    lastLayoutJson.current = json;
    setItems(itemsFromLayout(layout));
  }, [layoutData, layoutError, editing]);

  if (loading || !user) return null;

  const showFirstRun = showFirstRunOverride ?? (tx !== undefined && tx.length === 0);

  function startEdit() {
    if (!items) return;
    setPreEdit(items);
    setSaveErr(null);
    setEditing(true);
  }

  function cancelEdit() {
    if (preEdit) setItems(preEdit);
    setEditing(false);
    setSaveErr(null);
  }

  async function saveEdit() {
    if (!items) return;
    setSaving(true);
    setSaveErr(null);
    try {
      const layout = layoutFromItems(items);
      // A layout identical to the default is stored as NULL so the user
      // keeps following future default-layout improvements.
      const isDefault =
        JSON.stringify(layout.widgets) === JSON.stringify(DEFAULT_LAYOUT.widgets);
      await dashboardApi.put(isDefault ? null : layout);
      // Keep the mirror effect from re-creating items (new uids → widget
      // remount) when the next revalidate returns exactly what we saved.
      lastLayoutJson.current = JSON.stringify(layout.widgets);
      cacheSet("home:layout", layout);
      setEditing(false);
      setPreEdit(null);
    } catch (e) {
      setSaveErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell
      title="Übersicht"
      actions={
        editing ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setItems(itemsFromLayout(DEFAULT_LAYOUT))}
              className="px-2.5 py-1 text-xs rounded-md text-ink-300 hover:bg-ink-800"
              title="Auf das Standard-Layout zurücksetzen"
            >
              Zurücksetzen
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="px-2.5 py-1 text-xs rounded-md text-ink-300 hover:bg-ink-800"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saving}
              className="px-2.5 py-1 text-xs rounded-md bg-brand-500 text-ink-950 font-medium disabled:opacity-60"
            >
              {saving ? "…" : "Fertig"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEdit}
            className="px-2.5 py-1 text-xs rounded-md text-ink-300 hover:bg-ink-800"
            title="Widgets anordnen, hinzufügen oder entfernen"
          >
            ✎ Anpassen
          </button>
        )
      }
    >
      <div className="space-y-6">
        {saveErr && (
          <p className="text-sm text-bad-500" role="alert">
            Layout konnte nicht gespeichert werden: {saveErr}
          </p>
        )}
        {editing && (
          <p className="text-xs text-ink-400">
            Ziehe Widgets am ⠿-Griff (am Handy: gedrückt halten), entferne sie
            mit ✕ oder füge unten neue hinzu.
          </p>
        )}
        {showFirstRun && !editing && (
          <FirstRunCard
            hasBankConnection={bankConns.length > 0}
            onDismiss={() => setShowFirstRunOverride(false)}
          />
        )}
        {items && <Dashboard items={items} editing={editing} onChange={setItems} />}
      </div>
    </AppShell>
  );
}

function FirstRunCard({
  hasBankConnection,
  onDismiss,
}: {
  hasBankConnection: boolean;
  onDismiss?: () => void;
}) {
  return (
    <section className="card p-5 bg-gradient-to-br from-brand-900/30 to-ink-900 border-brand-700 relative">
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 text-ink-500 hover:text-ink-200 text-sm leading-none p-1"
          aria-label="Karte schließen"
          title="Schließen"
        >
          ✕
        </button>
      )}
      <h2 className="text-lg font-semibold text-ink-100 mb-1">Willkommen an Bord ⚓</h2>
      <p className="text-sm text-ink-300 mb-4">
        Dein Logbuch ist startklar. Drei kleine Schritte, dann hat Logbuch alles, um euch
        täglich zu zeigen, wohin das Geld segelt.
      </p>
      <ol className="space-y-2.5">
        <FirstRunStep
          n={1}
          done={hasBankConnection}
          to="/einstellungen#konten"
          title={hasBankConnection ? "Bank ist verbunden" : "Bank verbinden"}
          desc={
            hasBankConnection
              ? "Deine Umsätze werden automatisch geholt — oder jederzeit Datei hochladen."
              : "Bank verbinden — täglicher Abruf deiner Umsätze. Alternativ Datei hochladen unter Einstellungen → Konten & Banken."
          }
        />
        <FirstRunStep
          n={2}
          done={false}
          to="/transactions"
          title="Erste Buchung erfassen"
          desc="Zum Reinkommen: trag eine Miete, eine Rewe-Tüte oder dein Gehalt ein."
        />
        <FirstRunStep
          n={3}
          done={false}
          to="/budget#verwalten"
          title="Budgets feinjustieren"
          desc="Standard-Beträge passen vermutlich nicht zu 100 % — pass sie unter Budget → Verwalten an."
        />
      </ol>
    </section>
  );
}

function FirstRunStep({
  n,
  done,
  to,
  title,
  desc,
}: {
  n: number;
  done: boolean;
  to: string;
  title: string;
  desc: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-start gap-3 rounded-lg border border-ink-800 bg-ink-950/40 p-3 hover:border-brand-400 transition"
      >
        <span
          className={
            "shrink-0 inline-grid h-7 w-7 place-items-center rounded-full text-xs font-semibold " +
            (done ? "bg-good-500/20 text-good-500" : "bg-brand-500/20 text-brand-300")
          }
        >
          {done ? "✓" : n}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-ink-100">{title}</span>
          <span className="block text-xs text-ink-400">{desc}</span>
        </span>
        <span className="text-xs text-ink-500">→</span>
      </Link>
    </li>
  );
}
